import React, { useEffect, useMemo, useState } from "react";
import { Table, Badge, Button, Modal, Form } from "react-bootstrap";
import api from "api";
import styles from "../stripe-dashboard.module.css";

function PlansListTab({ plans, users, token, onRefresh }) {
  const [allPlans, setAllPlans] = useState(plans || []);
  const [loadingAll, setLoadingAll] = useState(false);
  const [detailsPlan, setDetailsPlan] = useState(null); // { planId, planName } | null
  const [editPlan, setEditPlan] = useState(null); // null | { ...plan }
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [deletePlanConfirm, setDeletePlanConfirm] = useState(null); // null | { id, name }
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAllPlans = async () => {
      if (!token) return;
      setLoadingAll(true);
      try {
        const res = await api.get("/v2/stripe/plans", {
          headers: { Authorization: `Bearer ${token}` },
          params: { include_inactive: true }
        });
        if (!cancelled) setAllPlans(res.data || []);
      } catch (err) {
        if (!cancelled) setAllPlans(plans || []);
      } finally {
        if (!cancelled) setLoadingAll(false);
      }
    };

    fetchAllPlans();
    return () => {
      cancelled = true;
    };
    // Trigger novamente após criação de plano (o array `plans` muda)
  }, [token, plans]);

  const isEmpty = !Array.isArray(allPlans) || allPlans.length === 0;

  const rows = useMemo(() => {
    if (isEmpty) return [];
    return allPlans.slice().sort((a, b) => (a.id || 0) - (b.id || 0));
  }, [allPlans, isEmpty]);

  const usersByPlanId = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(users)) return map;

    users.forEach(u => {
      if (u?.plan_id == null) return;
      const planId = Number(u.plan_id);
      if (Number.isNaN(planId)) return;
      if (!map.has(planId)) map.set(planId, []);
      map.get(planId).push(u);
    });

    return map;
  }, [users]);

  const selectedUsers = useMemo(() => {
    if (!detailsPlan) return [];
    const planId = Number(detailsPlan.planId);
    return usersByPlanId.get(planId) || [];
  }, [detailsPlan, usersByPlanId]);

  const fmtBRL = v =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v ?? 0);

  return (
    <div className={styles.card}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <p className={styles.cardTitle} style={{ margin: 0 }}>Planos Existentes</p>
        {onRefresh && token && (
          <Button
            variant="primary"
            size="sm"
            onClick={onRefresh}
            style={{ display: "inline-flex", gap: 8, alignItems: "center" }}
          >
            <i className="fas fa-sync" />
            Atualizar
          </Button>
        )}
      </div>

      <div className={styles.tableWrap} style={{ marginTop: 14 }}>
        {loadingAll ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "#94a3b8" }}>
            Carregando planos...
          </div>
        ) : isEmpty ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>
            Nenhum plano encontrado.
          </div>
        ) : (
          <Table className={styles.table} responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Preço (R$/mês)</th>
                <th>Armazenamento (GB)</th>
                <th>Limites</th>
                <th>AI</th>
                <th>Custom</th>
                <th>Usuários</th>
                <th>Ações</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => {
                const limits = `${p.max_projects ?? "—"} proj, ${p.max_reports ?? "—"} repts`;
                const planUsers = usersByPlanId.get(Number(p.id)) || [];
                const inactive = p.is_active === false;
                return (
                  <tr
                    key={p.id}
                    onClick={() => setDetailsPlan({ planId: p.id, planName: p.name })}
                    style={{
                      cursor: "pointer",
                      opacity: inactive ? 0.65 : 1,
                      background: inactive ? "#f8fafc" : undefined,
                    }}
                    title="Clique para ver usuários desse plano"
                  >
                    <td><span className={styles.mono}>{p.id}</span></td>
                    <td style={{ fontWeight: 700, color: "#0f172a" }}>{p.name}</td>
                    <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtBRL(p.price)}</td>
                    <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{p.storage ?? "—"}</td>
                    <td style={{ fontFamily: "'JetBrains Mono', monospace", color: "#334155" }}>{limits}</td>
                    <td>
                      <Badge bg={p.ai_feature ? "success" : "secondary"}>
                        {p.ai_feature ? "Sim" : "Não"}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={p.is_custom ? "primary" : "secondary"}>
                        {p.is_custom ? "Custom" : "Padrão"}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={planUsers.length > 0 ? "info" : "secondary"}>
                        {planUsers.length}
                      </Badge>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        style={{ padding: "0.35rem 0.65rem", fontSize: 12, marginRight: 8 }}
                        onClick={e => {
                          e.stopPropagation();
                          setEditError(null);
                          setEditPlan({
                            id: p.id,
                            name: p.name ?? "",
                            price: p.price ?? "",
                            storage: p.storage ?? "",
                            max_projects: p.max_projects ?? "",
                            max_reports: p.max_reports ?? "",
                            ai_feature: p.ai_feature ? 1 : 0,
                            is_custom: !!p.is_custom,
                          });
                        }}
                        disabled={!token}
                        title="Editar plano"
                      >
                        <i className="fas fa-edit" style={{ marginRight: 6 }} />
                        Editar
                      </button>
                      <button
                        type="button"
                        className={`${styles.button} ${styles.buttonDanger}`}
                        style={{ padding: "0.35rem 0.65rem", fontSize: 12 }}
                        onClick={e => {
                          e.stopPropagation();
                          setDeleteError(null);
                          setDeletePlanConfirm({ id: p.id, name: p.name ?? "" });
                        }}
                        disabled={!token}
                        title="Excluir/desativar plano"
                      >
                        <i className="fas fa-trash" style={{ marginRight: 6 }} />
                        Excluir
                      </button>
                    </td>
                    <td>
                      <Badge bg={inactive ? "secondary" : "success"}>
                        {inactive ? "Inativo" : "Ativo"}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>

      <Modal
        show={!!detailsPlan}
        onHide={() => setDetailsPlan(null)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-users me-2" />
            Usuários do plano: {detailsPlan?.planName || "—"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUsers.length === 0 ? (
            <div className="text-center text-muted py-4">
              Nenhum usuário encontrado para este plano.
            </div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {selectedUsers
                  .slice()
                  .sort((a, b) => (a.username || "").localeCompare(b.username || ""))
                  .map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 700, color: "#0f172a" }}>
                        {u.username || `#${u.id}`}
                      </td>
                      <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{u.email}</td>
                      <td>
                        <Badge
                          bg={
                            u.subscription_status === "active"
                              ? "success"
                              : u.subscription_status
                                ? "warning"
                                : "secondary"
                          }
                        >
                          {u.subscription_status || "inactive"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailsPlan(null)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal editar plano */}
      <Modal
        show={!!editPlan}
        onHide={() => {
          if (editLoading) return;
          setEditPlan(null);
          setEditError(null);
        }}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-edit me-2" />
            Editar plano #{editPlan?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editPlan ? null : (
            <form
              className={styles.formBody}
              onSubmit={async e => {
                e.preventDefault();
                if (!token) return;
                setEditLoading(true);
                setEditError(null);

                try {
                  const payload = {
                    name: editPlan.name,
                    price: Number(editPlan.price),
                    storage: Number(editPlan.storage),
                    max_projects:
                      editPlan.max_projects === "" ? null : Number(editPlan.max_projects),
                    max_reports:
                      editPlan.max_reports === "" ? null : Number(editPlan.max_reports),
                    ai_feature: Number(editPlan.ai_feature),
                    is_custom: !!editPlan.is_custom,
                  };

                  // Campos null não são enviados pelo backend (Pydantic exclude_none)
                  Object.keys(payload).forEach(k => {
                    if (payload[k] === null) delete payload[k];
                  });

                  await api.put(`/v2/stripe/plans/${editPlan.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                  });

                  setEditPlan(null);
                  if (onRefresh) onRefresh();
                } catch (err) {
                  setEditError(err.response?.data?.detail || "Erro ao atualizar plano.");
                } finally {
                  setEditLoading(false);
                }
              }}
            >
              <div className={styles.formGrid2}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Nome do Plano</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    value={editPlan.name}
                    onChange={e => setEditPlan(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Preço (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    className={styles.formControl}
                    value={editPlan.price}
                    onChange={e => setEditPlan(prev => ({ ...prev, price: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className={styles.formGrid3}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Armazenamento (GB)</label>
                  <input
                    type="number"
                    step="0.01"
                    className={styles.formControl}
                    value={editPlan.storage}
                    onChange={e => setEditPlan(prev => ({ ...prev, storage: e.target.value }))}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Máx. Projetos</label>
                  <input
                    type="number"
                    className={styles.formControl}
                    value={editPlan.max_projects}
                    onChange={e => setEditPlan(prev => ({ ...prev, max_projects: e.target.value }))}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Máx. Relatórios</label>
                  <input
                    type="number"
                    className={styles.formControl}
                    value={editPlan.max_reports}
                    onChange={e => setEditPlan(prev => ({ ...prev, max_reports: e.target.value }))}
                  />
                </div>
              </div>

              <div className={styles.formGrid2} style={{ marginTop: 6 }}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>AI Feature</label>
                  <Form.Select
                    value={editPlan.ai_feature ? "1" : "0"}
                    onChange={e =>
                      setEditPlan(prev => ({ ...prev, ai_feature: e.target.value === "1" ? 1 : 0 }))
                    }
                    className={styles.formControl}
                  >
                    <option value="1">Sim</option>
                    <option value="0">Não</option>
                  </Form.Select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Customizado</label>
                  <Form.Select
                    value={editPlan.is_custom ? "true" : "false"}
                    onChange={e =>
                      setEditPlan(prev => ({ ...prev, is_custom: e.target.value === "true" }))
                    }
                    className={styles.formControl}
                  >
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </Form.Select>
                </div>
              </div>

              {editError && (
                <div style={{ marginTop: 14, color: "#dc2626", fontWeight: 600 }}>
                  {editError}
                </div>
              )}

              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={editLoading}
                >
                  {editLoading ? (
                    <>
                      <div className={styles.spinner} style={{ width: 14, height: 14, borderWidth: 2 }} />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save" style={{ marginRight: 6 }} />
                      Salvar
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal confirmar exclusão */}
      <Modal
        show={!!deletePlanConfirm}
        onHide={() => {
          if (deleteLoading) return;
          setDeletePlanConfirm(null);
          setDeleteError(null);
        }}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-trash me-2" />
            Desativar plano
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deletePlanConfirm && (
            <>
              <div style={{ color: "#0f172a", fontWeight: 700 }}>
                {deletePlanConfirm.name || `Plano #${deletePlanConfirm.id}`}
              </div>
              <div style={{ marginTop: 10, color: "#64748b" }}>
                Esta ação irá desativar o plano (soft-delete) e tentar desativar o Price no Stripe.
              </div>
              {deleteError && (
                <div style={{ marginTop: 14, color: "#dc2626", fontWeight: 600 }}>
                  {deleteError}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              if (deleteLoading) return;
              setDeletePlanConfirm(null);
              setDeleteError(null);
            }}
            disabled={deleteLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (!token || !deletePlanConfirm) return;
              setDeleteLoading(true);
              setDeleteError(null);
              try {
                await api.delete(`/v2/stripe/plans/${deletePlanConfirm.id}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                setDeletePlanConfirm(null);
                if (onRefresh) onRefresh();
              } catch (err) {
                setDeleteError(err.response?.data?.detail || "Erro ao excluir plano.");
              } finally {
                setDeleteLoading(false);
              }
            }}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Desativando..." : "Desativar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export { PlansListTab };

